/** Loads a BiomechTrack v1 JSON document and samples interpolated frames by time. */
import { lerp } from "./vec.ts";
import type {
  BiomechFrame,
  BiomechFramePerson,
  BiomechJoint,
  BiomechTrack,
  BiomechTrackPerson,
  JointId,
  ValidationSummary,
  Vec3,
} from "./types.ts";

function lerpScalar(a: number, b: number, s: number): number {
  return a + (b - a) * s;
}

function lerpJoint(a: BiomechJoint, b: BiomechJoint, s: number): BiomechJoint {
  return {
    p: lerp(a.p, b.p, s),
    uv:
      a.uv && b.uv
        ? [lerpScalar(a.uv[0], b.uv[0], s), lerpScalar(a.uv[1], b.uv[1], s)]
        : a.uv ?? b.uv ?? null,
    f: lerp(a.f, b.f, s),
    m: lerp(a.m, b.m, s),
    load: lerpScalar(a.load, b.load, s),
  };
}

function lerpPerson(a: BiomechFramePerson, b: BiomechFramePerson, s: number): BiomechFramePerson {
  const joints: Partial<Record<JointId, BiomechJoint>> = {};
  for (const [key, ja] of Object.entries(a.joints)) {
    if (!ja) continue;
    const jb = b.joints[key as JointId];
    joints[key as JointId] = jb ? lerpJoint(ja, jb, s) : ja;
  }
  return {
    id: a.id,
    com: lerp(a.com, b.com, s),
    joints,
    residual: {
      f: lerp(a.residual?.f ?? ([0, 0, 0] as Vec3), b.residual?.f ?? ([0, 0, 0] as Vec3), s),
      m: lerp(a.residual?.m ?? ([0, 0, 0] as Vec3), b.residual?.m ?? ([0, 0, 0] as Vec3), s),
    },
    comUv:
      a.comUv && b.comUv
        ? [lerpScalar(a.comUv[0], b.comUv[0], s), lerpScalar(a.comUv[1], b.comUv[1], s)]
        : a.comUv ?? b.comUv ?? null,
  };
}

/** Linear interpolation between two solved frames. Contacts snap to the nearer frame. */
export function interpolateFrames(a: BiomechFrame, b: BiomechFrame, s: number): BiomechFrame {
  const clamped = Math.max(0, Math.min(1, s));
  const persons: BiomechFramePerson[] = [];
  for (const pa of a.persons) {
    const pb = b.persons.find((p) => p.id === pa.id);
    persons.push(pb ? lerpPerson(pa, pb, clamped) : pa);
  }
  return {
    t: lerpScalar(a.t, b.t, clamped),
    persons,
    contacts: clamped < 0.5 ? a.contacts : b.contacts,
  };
}

export function isBiomechTrack(value: unknown): value is BiomechTrack {
  const t = value as BiomechTrack | null;
  return (
    !!t &&
    typeof t === "object" &&
    t.format === "acro.biomech.track" &&
    Array.isArray(t.frames)
  );
}

export class BiomechTrackPlayer {
  readonly track: BiomechTrack;
  private readonly frames: BiomechFrame[];

  constructor(track: BiomechTrack) {
    this.track = track;
    this.frames = (track.frames ?? []).slice().sort((a, b) => a.t - b.t);
  }

  /** Accepts a JSON string or a parsed object; returns null when it is not a v1 track. */
  static parse(input: unknown): BiomechTrackPlayer | null {
    let value = input;
    if (typeof value === "string") {
      try {
        value = JSON.parse(value);
      } catch {
        return null;
      }
    }
    if (!isBiomechTrack(value)) return null;
    return new BiomechTrackPlayer(value);
  }

  get frameCount(): number {
    return this.frames.length;
  }

  get startTime(): number {
    return this.frames[0]?.t ?? this.track.startTime ?? 0;
  }

  get endTime(): number {
    return this.frames[this.frames.length - 1]?.t ?? this.startTime;
  }

  get duration(): number {
    return Math.max(0, this.endTime - this.startTime);
  }

  get method(): "opensim" | "estimate" | "mujoco" {
    return this.track.method ?? "estimate";
  }

  get persons(): BiomechTrackPerson[] {
    return this.track.persons ?? [];
  }

  get validation(): ValidationSummary | null {
    return this.track.validation ?? null;
  }

  /** Interpolated frame at time t (clamped to the track range). null when empty. */
  sample(t: number): BiomechFrame | null {
    const n = this.frames.length;
    if (n === 0) return null;
    if (n === 1) return this.frames[0]!;
    const time = Number.isFinite(t) ? t : 0;
    if (time <= this.frames[0]!.t) return this.frames[0]!;
    if (time >= this.frames[n - 1]!.t) return this.frames[n - 1]!;

    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const midIdx = (lo + hi) >> 1;
      if (this.frames[midIdx]!.t <= time) lo = midIdx;
      else hi = midIdx;
    }
    const a = this.frames[lo]!;
    const b = this.frames[hi]!;
    const span = b.t - a.t;
    const s = span > 1e-9 ? (time - a.t) / span : 0;
    return interpolateFrames(a, b, s);
  }
}
